import { Stack } from '@mui/material';
import type { ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';

import { BoxFlex, CopyQRCode, PaymentFooter } from '@woovi/ui';

import type { PaymentQrCode_pixQrCode$key } from './__generated__/PaymentQrCode_pixQrCode.graphql.ts';
import type { PaymentQrCodeContent_pixQrCode$key } from './__generated__/PaymentQrCodeContent_pixQrCode.graphql.ts';
import PaymentLinkQrCode from './PaymentLinkQrCode.tsx';
import PaymentLinkActiveOpenBankInfo from '../active/paymentLinkActive/PaymentLinkActiveOpenBankInfo.tsx';
import PaymentActivePixCobValue from '../active/pixCob/PaymentActivePixCobValue.tsx';
import PaymentDeleted from '../deleted/PaymentDeleted.tsx';
import PaymentCompanyLogo from '../logo/PaymentCompanyLogo.tsx';
import PaymentLinkComment from '../paymentLink/PaymentLinkComment.tsx';
import PaymentLinkShareButton from '../paymentLink/PaymentLinkShareButton.tsx';
import PaymentLinkTransactionDetails from '../PaymentLinkTransactionDetails.tsx';
import PaymentTestAccountBanner from '../PaymentTestAccountBanner.tsx';
import PaymentCompanySupport from '../support/PaymentCompanySupport.tsx';

type PaymentQrCodeProps = {
  pixQrCode: PaymentQrCode_pixQrCode$key;
};

const PaymentQrCodeContent = (props: PaymentQrCodeProps): ReactNode => {
  const pixQrCode = useFragment<PaymentQrCodeContent_pixQrCode$key>(
    graphql`
      fragment PaymentQrCodeContent_pixQrCode on PixQrCode {
        id
        brCode
        isRemoved
        ...PaymentLinkComment_pixQrCode
        ...PaymentLinkQrCode_pixQrCode
        ...PaymentLinkShareButton_pixQrCode
        ...PaymentLinkTransactionDetails_pixQrCode
        ...PaymentActivePixCobValue_pixQrCode
        ...PaymentDeleted_pixQrCode
      }
    `,
    props.pixQrCode,
  );

  if (pixQrCode.isRemoved) {
    return <PaymentDeleted charge={null} pixQrCode={pixQrCode} />;
  }

  return (
    <BoxFlex
      sx={{
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <BoxFlex
        sx={{
          justifyContent: 'center',
          flexDirection: 'column',
          alignItems: 'center',
          display: 'flex',
          fontSize: '20px',
          fontWeight: 'bold',
        }}
      >
        <PaymentLinkComment charge={null} pixQrCode={pixQrCode} />
      </BoxFlex>

      <BoxFlex
        sx={{
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Stack>
          <PaymentActivePixCobValue pixQrCode={pixQrCode} />
        </Stack>
        <Stack
          sx={{
            gap: 1,
            maxWidth: '332px',
            width: '100%',
          }}
        >
          <PaymentLinkQrCode pixQrCode={pixQrCode} />
          <CopyQRCode value={pixQrCode.brCode} />
          <PaymentLinkActiveOpenBankInfo />
          <PaymentLinkShareButton charge={null} pixQrCode={pixQrCode} />
        </Stack>
        <PaymentLinkTransactionDetails charge={null} pixQrCode={pixQrCode} />
      </BoxFlex>
    </BoxFlex>
  );
};

const PaymentQrCode = (props: PaymentQrCodeProps): ReactNode => {
  const pixQrCode = useFragment<PaymentQrCode_pixQrCode$key>(
    graphql`
      fragment PaymentQrCode_pixQrCode on PixQrCode {
        id
        company {
          projectType
          ...PaymentCompanyLogo_company
          ...PaymentCompanySupport_company
        }
        ...PaymentTestAccountBanner_pixQrCode
        ...PaymentQrCodeContent_pixQrCode
      }
    `,
    props.pixQrCode,
  );

  return (
    <>
      <PaymentTestAccountBanner charge={null} pixQrCode={pixQrCode} />
      <PaymentCompanyLogo company={pixQrCode.company} />
      <PaymentQrCodeContent pixQrCode={pixQrCode} />
      <PaymentCompanySupport company={pixQrCode.company} />
      <PaymentFooter projectType={pixQrCode.company.projectType} />
    </>
  );
};

export default PaymentQrCode;
